import React, { useCallback, useEffect, useMemo } from "react";

import { useStore } from "../../stores/store";
import { SearchTab } from "../../interfaces/headerSearch";
import getSearchOptions from "./getSearchOptions";
import { getAddressSuggestions } from "./getAddressSuggestions.ts";

import _ from "lodash";
import AutoComplete from "./DefaultAutoComplete.tsx";
import ParcelAutoComplete from "./ParcelAutoComplete.tsx";
import { getAddressDataFromMagicKeys } from "./getAddressDataFromMagicKeys.ts";

import Graphic from "@arcgis/core/Graphic";

interface Option {
  label: string;
  value: string;
}

interface HeaderSearchProps {
  tabs: SearchTab[];
  singleSelectionMode?: boolean;
  headerTitle?: string;
  onSearch?: (data: any) => void;
}

export const HeaderSearch = (props: HeaderSearchProps) => {
  const {
    mapView,
    startSearch,
    isResultsLoading,
    activeSearchTab,
    setActiveSearchTab,
    mapLoaded,
    searchValue,
    setSearchValue,
    selectedParcelItems,
    setSelectedParcelItems,
    isLocal,
    isMobile,
    setResults,
    setActiveResultsType,
    bufferLayer,
  } = useStore();

  const [parcelItems, setParcelItems] = React.useState([] as Option[]);

  const selectedTab = useMemo(() => {
    return props.tabs.find((tab) => activeSearchTab === tab.value);
  }, [props.tabs, activeSearchTab]);

  const loadOptionsDebounced = useCallback(
    async (inputVal: string) => {
      if (selectedTab?.value === "address") {
        // Just realised we'll need to conform the results from getAddressSuggestions a little better
        // onSearch() is getting called with the value (magicKey) not the label (address)
        const addressSuggestions = await getAddressSuggestions(inputVal);
        console.log("Address suggestions:", addressSuggestions);
        return addressSuggestions;
      }
      const options = await getSearchOptions(inputVal, selectedTab);
      return options as any[];
    },
    [selectedTab],
  );

  const onAddressSearch = useCallback(
    async (magicKeys: string[]) => {
      setActiveResultsType("address");
      const graphics = [];
      const magicKeyResults = await getAddressDataFromMagicKeys(magicKeys);
      for (const magicKeyResult of magicKeyResults) {
        if (magicKeyResult) {
          const graphic = new Graphic({
            geometry: {
              type: "point" as any,
              longitude: magicKeyResult.location.x,
              latitude: magicKeyResult.location.y,
              spatialReference: { wkid: 4326 },
            },
            attributes: {
              address: magicKeyResult.address,
              latitude: magicKeyResult.location.y,
              longitude: magicKeyResult.location.x,
            },
            symbol: {
              type: "simple-marker" as any,
              color: [0, 255, 255, 0.5],
              size: "14px",
              outline: {
                color: "cyan",
                width: 3,
              },
            },
          });
          graphics.push(graphic);
        }
      }
      if (mapView) {
        setResults(graphics);
        bufferLayer?.addMany(graphics);
        await mapView.goTo({ target: graphics, animate: false });
        mapView.zoom = 14;
        if (props.onSearch) {
          props.onSearch(magicKeyResults);
        }
      }
    },
    [mapView, setResults, setActiveResultsType, bufferLayer],
  );

  useEffect(() => {
    // get default tab
    const defaultTab = props.tabs.find((tab) => tab.default);
    if (defaultTab) {
      setActiveSearchTab(defaultTab.value);
    }
  }, [props.tabs]);

  const onSearch = useCallback(
    async (searchVal: string | null | any[]) => {
      if (selectedTab?.value === "address") {
        return;
      }
      if (searchVal === null) {
        searchVal = searchValue;
      }
      if (searchVal === null) {
        return;
      }

      const escapedSearchValue = searchVal.replace(/'/g, "''");
      const where = selectedTab?.getWhereClause
        ? await selectedTab?.getWhereClause(
            escapedSearchValue,
            selectedParcelItems,
          )
        : `${selectedTab?.field} like '${escapedSearchValue}%'`;
      startSearch(where, selectedTab as SearchTab, props.onSearch);
    },
    [searchValue, selectedTab, selectedParcelItems],
  );

  const filteredTabs = props.tabs.filter((tab) => {
    // if (tab.internalOnly) {
    // return isLocal;
    // }
    if (!tab.isMobile) {
      if (isMobile) {
        return false;
      }
    }
    return true;
  });

  useEffect(() => {
    if (!mapLoaded) {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const parcel = params.get("parcel");
    if (!parcel) {
      return;
    }
    const parcelNoDashes = parcel.replace(/-/g, "");
    const isValidParcel =
      parcelNoDashes &&
      parcelNoDashes.length === 10 &&
      !isNaN(parseInt(parcelNoDashes));
    if (!isValidParcel) {
      return;
    }

    const formattedParcel =
      parcelNoDashes.slice(0, 2) +
      "-" +
      parcelNoDashes.slice(2, 4) +
      "-" +
      parcelNoDashes.slice(4, 7) +
      "-" +
      parcelNoDashes.slice(7);

    setSearchValue(formattedParcel);
    onSearch(formattedParcel);
  }, [mapLoaded]);

  return (
    <>
      <div className="">
        <div className="flex items-center font-normal">
          <h4 className="block text-mchenry-orange mr-2 pl-2 text-sm">
            {props.headerTitle ?? "SEARCH BY"}:
          </h4>
          <div role="tablist" className="tabs tabs-lifted tabs-sm">
            {filteredTabs.map((tab, i) => (
              <a
                role="tab"
                onClick={() => {
                  setActiveSearchTab(tab.value);
                  setSearchValue("");
                  setSelectedParcelItems([]);
                }}
                className={`tab text-white text-sm font-thin [--tab-bg:#393838] [--tab-border-color:transparent] ${
                  tab.value === selectedTab?.value ? "tab-active" : ""
                }`}
                key={i}
              >
                {tab.label}
              </a>
            ))}
          </div>
        </div>
        <div className="bg-mchenry-gray p-2 flex rounded-md items-center w-full">
          {props.tabs
            .filter((tab) => {
              return tab.value === selectedTab?.value;
            })
            .map((tab, i) => (
              <React.Fragment key={i}>
                {selectedTab?.customUI ? (
                  <div className="flex-auto">
                    <selectedTab.customUI />
                  </div>
                ) : (
                  <div className="flex-auto md:w-[420px]">
                    {selectedTab?.label === "Parcel" ? (
                      <>
                        {isMobile || props.singleSelectionMode ? (
                          <AutoComplete
                            getOptions={loadOptionsDebounced}
                            placeholder={
                              selectedTab?.placeholder || "Search..."
                            }
                            setSearchValue={setSearchValue}
                            searchValue={searchValue}
                            onSearch={onSearch}
                            onAddressSearch={onAddressSearch}
                            searchLabel={selectedTab?.label || ""}
                          />
                        ) : (
                          <ParcelAutoComplete
                            getOptions={loadOptionsDebounced}
                            placeholder={
                              selectedTab?.placeholder || "Search..."
                            }
                            onSearch={onSearch}
                            searchLabel={selectedTab?.label || ""}
                            parcelItems={parcelItems}
                            setParcelItems={setParcelItems}
                          />
                        )}
                      </>
                    ) : (
                      <AutoComplete
                        getOptions={loadOptionsDebounced}
                        placeholder={selectedTab?.placeholder || "Search..."}
                        setSearchValue={setSearchValue}
                        searchValue={searchValue}
                        onSearch={onSearch}
                        searchLabel={selectedTab?.label || ""}
                        onAddressSearch={onAddressSearch}
                      />
                    )}
                  </div>
                )}
              </React.Fragment>
            ))}

          <div
            className={`flex-none hidden md:flex ${
              props.singleSelectionMode ? "invisible" : ""
            }`}
          >
            <button
              onClick={() => {
                onSearch(null);
                if (selectedTab?.value === "address") {
                  // console.log('Search button clicked for address tab');
                  // console.log('Current search value:', searchValue);
                  getAddressSuggestions(searchValue, 100).then(
                    (suggestions) => {
                      onAddressSearch(suggestions.map((s) => s.value));
                    },
                  );
                  return;
                } else {
                  if (searchValue !== "") {
                    onSearch(null);
                  } else {
                    if (selectedTab?.customUI) {
                      onSearch(null);
                    }
                  }
                }
              }}
              className="ml-2 w-28 text-xs btn-athena py-1.5"
            >
              {isResultsLoading ? "SEARCHING" : "SEARCH"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
